import { useRef } from "react";

import { HistoryEntityType } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { ActionButton, Dialog } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { PointChartIcon } from "@eisox/icons";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { useSelector } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";

import {
  exportData,
  formatUnit,
  getDateOptions,
  getValvePointLabel,
  getValvePointOptions,
  transformHistoryData,
} from "~/helpers/history.helper";
import { fetchBuildingHistory } from "~/hooks/use-building-history";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";
import type { EnhancedValve } from "~/types/valve.type";

interface ExportFormValues {
  data: string[];
  valve: string;
  start: string;
  end: string;
}

interface DataExportPopupProps extends Dialog.RootProps {
  buildingId: string;
  data: string[];
  valves: (EnhancedValve & { mac: string })[];
  valveId: string;
  date: string;
}

export const DataExportPopup: React.FC<DataExportPopupProps> = ({
  buildingId,
  data: dataProp,
  date: dateProp,
  valves,
  valveId,
  open: openProp,
  // oxlint-disable-next-line typescript/unbound-method
  onOpenChange,
  defaultOpen = false,
  children,
  ...props
}) => {
  const formatRef = useRef<"csv" | "xlsx">("csv");
  const queryClient = useQueryClient();
  const { role } = useBuildingContext("DataExportPopup");

  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen,
  });

  const form = useAppForm({
    defaultValues: {
      data: dataProp,
      valve: valveId,
      start: dateProp,
      end: dayjs(dateProp).endOf("day").toISOString(),
    } satisfies ExportFormValues,
    onSubmit: async ({ value }) => {
      const historyData = await fetchBuildingHistory(
        queryClient,
        buildingId,
        value.start,
        value.end,
        HistoryEntityType.VALVE,
        value.data.map(point => ({ entityId: value.valve, point })),
      );
      const valve = valves.find(v => v.id === value.valve);
      if (!valve?.mac) return;
      const rows = transformHistoryData(historyData, value.data, role).map(d => {
        const row: Record<string, string | number> = {
          [m.date()]: dayjs(d.date).format("L - LT"),
        };
        for (const point of value.data) {
          const pointValue = d[point];
          if (pointValue != null) {
            row[`${getValvePointLabel(point)} (${formatUnit(point)})`] = pointValue;
          }
        }
        return row;
      });
      exportData(rows, valve.mac.replace(/:/g, ""), formatRef.current);
    },
  });

  const selectedPoints = useSelector(form.store, state => state.values.data);
  const selectedValve = useSelector(form.store, state => state.values.valve);
  const startDate = useSelector(form.store, state => state.values.start);

  const dataOptions = getValvePointOptions();
  const valveOptions = valves.map(v => ({ value: v.id, name: v.mac }));
  const startDateOptions = getDateOptions();
  const endDateOptions = getDateOptions(false).filter(o => dayjs(o.value).isAfter(startDate));

  const handleOpenChange = (open: boolean) => {
    form.reset();
    setOpen(open);
  };

  const exportFormatLabels: Record<"csv" | "xlsx", () => string> = {
    csv: () => `${m.export()} (.csv)`,
    xlsx: () => `${m.export()} (.xlsx)`,
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange} {...props}>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <Dialog.Content title={m.export_data()} icon={<PointChartIcon />}>
        <form
          className="w-full"
          onSubmit={e => {
            e.preventDefault();
            void form.handleSubmit();
          }}
        >
          <div className="gap-x-5 gap-y-5 *:w-60 grid grid-flow-row grid-cols-2 grid-rows-[1fr_1fr]">
            <form.AppForm>
              <form.AppField name="data">
                {field => (
                  <field.Select multiple searchInput={false} options={dataOptions} classNames={{ control: "w-full" }} />
                )}
              </form.AppField>
              <form.AppField name="valve">
                {field => (
                  <field.Select
                    multiple={false}
                    searchInput={false}
                    disabled={!!valveId}
                    options={valveOptions}
                    classNames={{ control: "w-full" }}
                  />
                )}
              </form.AppField>
              <form.AppField name="start">
                {field => (
                  <field.Select
                    multiple={false}
                    searchInput={false}
                    options={startDateOptions}
                    classNames={{ control: "w-full" }}
                  />
                )}
              </form.AppField>
              <form.AppField name="end">
                {field => (
                  <field.Select
                    multiple={false}
                    searchInput={false}
                    options={endDateOptions}
                    classNames={{ control: "w-full" }}
                  />
                )}
              </form.AppField>
            </form.AppForm>
          </div>
          <div className="mt-10 gap-x-2.5 flex items-center justify-center">
            <Dialog.Close asChild>
              <ActionButton variant="cancel">{m.cancel()}</ActionButton>
            </Dialog.Close>
            {(["csv", "xlsx"] as const).map(format => (
              <ActionButton
                key={format}
                type="submit"
                disabled={selectedPoints.length === 0 || !selectedValve}
                onClick={() => {
                  formatRef.current = format;
                }}
              >
                {exportFormatLabels[format]()}
              </ActionButton>
            ))}
          </div>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export type { DataExportPopupProps };
